/**
 * `<caos-tree>` — a hierarchy in, a choice out. The one hierarchical primitive: a navigation rail, a
 * Setup console's section list, a role hierarchy, or any other grouped/nested list of things a person
 * reads and picks from, composed from this instead of hand-built one row at a time.
 * (Catalog: Navigation › Tree — a hierarchy of nodes, grouped or nested, that a person can move through and choose from.)
 *
 * Data in: the `nodes` PROPERTY (array — cannot ride an attribute) of `CaosTreeNode`. A node with
 * `children` is a BRANCH, rendering as a heading over its own nested list (recursing to any depth); a
 * node with none is a LEAF, rendering as an `<a href>` when it carries an `href` (real link
 * behaviour: middle-click, ctrl/cmd-click and "open in new tab" all work) and a `<button>` otherwise.
 *
 * ── IT IS A TREE, SO IT ANSWERS TO A TREE'S KEYBOARD (CAOS-591) ──────────────
 * Every node — branch and leaf alike — is a `treeitem`, and the nested lists are `group`s, each node
 * carrying its level, how many siblings it has and which one it is. That is the structure a screen
 * reader announces; before this, a branch was an inert heading that stood beside its children rather
 * than over them, and nothing said how deep anything was.
 *
 * The component takes ONE tab stop, not one per row. Inside it the arrow keys move a roving focus:
 * down and up through the nodes currently on screen, right to open a closed branch and then into it,
 * left to close an open branch and then out to its parent, `Home` and `End` to the first and last,
 * and typing letters jumps to the next node whose label starts with them. `Enter` opens a leaf;
 * `Enter` or `Space` on a branch opens or closes it. A rail of forty rows is four arrow presses from
 * the fourth row rather than forty tab stops deep.
 *
 * A branch is therefore FOCUSABLE. An inert heading cannot carry any of this, and a tree whose
 * branches cannot be reached by keyboard is a tree only in how it looks.
 *
 * ── OPENING AND CLOSING IS DECLARED, AND NOTHING STARTS HIDDEN ───────────────
 * `collapsible` says a reader may open and close branches. A tree without it holds every branch open
 * and says nothing about expansion, which is what a permanently open branch should say rather than
 * claiming to be openable. A tree WITH it still starts with every branch open: a hierarchy exists to
 * be seen, and a component that hid descendants the moment it became collapsible would take away on
 * arrival exactly what it was placed to show.
 *
 * A live `filter` holds every branch open for as long as it runs, so a surviving match is never left
 * behind a closed branch, and closing state is remembered rather than discarded — clearing the filter
 * returns the tree to the shape the reader left it in.
 *
 * ── WHAT IT DOES NOT DECIDE ──────────────────────────────────────────────────
 * It holds no routing and no selection logic. `selected-id` marks the current node; choosing one
 * raises an event and the host decides what that means.
 *
 * ── A SIDE NAVIGATION IS THIS LIST PLUS TWO PIECES (CAOS-592) ────────────────
 * The `heading` PROPERTY (the console's name, its mark, and the way back to its landing) and the
 * `filterable` ATTRIBUTE (a find box this component owns and filters itself). Both are optional: a
 * bare list is still a bare list. The current row is painted from the design system's state tokens
 * (CAOS-586) so every current row on the platform agrees.
 *
 * Events out (bubbling + composed):
 *   `tree-choose` — detail `{ id, href? }`, a leaf was chosen.
 *   `tree-toggle` — detail `{ id, expanded }`, a branch was opened or closed by the reader.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { makeIcon, isIconName } from './icons.js';

export interface CaosTreeNode {
  id: string;
  label: string;
  /** A glyph name from the shared icon vocabulary (CAOS-324). Omitted ⇒ no leading mark. */
  icon?: string;
  /** A real link target. Present ⇒ the row is an `<a href>`; absent ⇒ a `<button>`. */
  href?: string;
  /** A short trailing marker, e.g. "Soon" or a count of what sits under this node. */
  badge?: string;
  /** Present (even empty) ⇒ this node is a branch over its own nested list, not a leaf. */
  children?: readonly CaosTreeNode[];
}

/**
 * CAOS-592 — the rail's own heading: the name of the console this list belongs to, and the way back
 * to its landing. A side navigation is the heading, the find box and the list together; a list on its
 * own is not one, which is why every console that wanted a rail wrote these two pieces again by hand.
 */
export interface CaosTreeHeading {
  /** The console's name — "Setup", "My Profile". */
  label: string;
  /** A glyph name from the shared icon vocabulary (CAOS-324). Omitted ⇒ no leading mark. */
  icon?: string;
  /** Where the heading goes — the console's landing. Present ⇒ a real link, as a row with an href is. */
  href?: string;
  /**
   * The `selected-id` value that means "the landing is what you are looking at", so the heading reads
   * as current. Omitted ⇒ the empty id, which is what a console's own landing route resolves to.
   */
  id?: string;
}

const sheet = makeSheet(`
  :host { display: block; }

  .groups {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .none {
    margin: 0;
    padding: 0 var(--caos-space-2);
    font-size: 12px;
    color: var(--caos-color-text-muted);
  }

  /* A top-level branch stands apart from what precedes it, the way a Setup rail's sections do. */
  .groups > .node.is-branch { margin-top: var(--caos-space-4); }
  .groups > .node.is-branch:first-child { margin-top: 0; }

  .node { display: flex; flex-direction: column; gap: 2px; }
  .node:focus { outline: none; }
  .node:focus-visible { outline: none; }
  /* Focus is drawn on the row inside the node, since the node itself is the full-width wrapper. */
  .node:focus-visible > .heading,
  .node:focus-visible > .item { box-shadow: var(--caos-focus-ring); }

  .heading {
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: var(--caos-font-mono, monospace);
    font-size: var(--caos-text-label, 11px);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--caos-color-text-muted);
    padding: 0 var(--caos-space-2);
    padding-left: calc(var(--caos-space-2) + var(--depth, 0) * 12px);
    border-radius: var(--caos-radius-sm);
  }
  :host([collapsible]) .heading { cursor: pointer; }
  :host([collapsible]) .heading:hover { background: var(--caos-color-surface-2); }

  /* A branch that is also a destination: it reads as its heading, and behaves as a link. */
  .heading__link {
    color: inherit;
    text-decoration: none;
    text-underline-offset: 2px;
    cursor: pointer;
  }
  .heading__link:hover { color: var(--caos-color-text); text-decoration: underline; }

  /* The open/closed marker. Only a collapsible tree has anything to mark. */
  .twisty {
    display: inline-flex;
    flex-shrink: 0;
    width: 10px;
    justify-content: center;
    transition: transform 0.12s ease;
  }
  .node[aria-expanded="false"] > .heading > .twisty,
  .node[aria-expanded="false"] > .item > .twisty { transform: rotate(-90deg); }

  .list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 2px; }

  .item {
    display: flex;
    align-items: center;
    gap: 9px;
    width: 100%;
    padding: 7px var(--caos-space-2);
    padding-left: calc(var(--caos-space-2) + var(--depth, 0) * 12px);
    border: none;
    background: transparent;
    border-radius: var(--caos-radius-sm);
    font-family: var(--caos-font);
    font-size: 13px;
    text-align: left;
    color: var(--caos-color-text-muted);
    text-decoration: none;
    cursor: pointer;
    transition: background 0.12s ease, color 0.12s ease;
  }
  .item-glyph { display: inline-flex; flex-shrink: 0; }
  .item-glyph svg { width: 14px; height: 14px; display: block; }
  .item__label { flex: 1; min-width: 0; }

  /* CAOS-592 — hovered and current rows are painted from the design system's STATE axis (CAOS-586),
     not from this component's own mix of accent over surface. Every list that marks a current row
     reads the same value, and a theme moves all of them at once. */
  .item:hover { background: var(--caos-state-hover-bg); }
  .item.is-active {
    background: var(--caos-state-selected-bg);
    color: var(--caos-color-text);
    box-shadow: inset 2px 0 0 var(--caos-color-accent);
  }
  .item:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  /* CAOS-592 — the rail's heading: the console's name, its mark, and the way back to its landing.
     Ported from the two consoles that each drew it themselves. */
  .rail-heading { display: block; }
  .rail-heading__link {
    display: flex;
    align-items: center;
    gap: var(--caos-space-2);
    padding: var(--caos-space-1, 4px) var(--caos-space-2);
    font-family: var(--caos-font-display);
    font-weight: 700;
    font-size: 16px;
    color: var(--caos-color-text);
    text-decoration: none;
  }
  .rail-heading__link:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  .rail-heading__glyph { display: inline-flex; color: var(--caos-color-accent); }
  .rail-heading__glyph svg { width: 16px; height: 16px; display: block; }

  /* The find box — narrows the list as you type. A list long enough to need a rail is unusable
     without one, which is why the console that had one wrote it by hand. */
  .find {
    display: flex;
    align-items: center;
    gap: var(--caos-space-2);
    padding: 6px 9px;
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
  }
  .find:focus-within { border-color: var(--caos-color-accent); }
  .find__glyph { display: inline-flex; color: var(--caos-color-text-muted); }
  .find__glyph svg { width: 14px; height: 14px; display: block; }
  .find__input {
    flex: 1;
    min-width: 0;
    background: transparent;
    border: none;
    outline: none;
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: 12px;
  }
  .find__input::placeholder { color: var(--caos-color-text-muted); }

  .badge {
    font-family: var(--caos-font-mono, monospace);
    font-size: 9px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--caos-color-text-muted);
    border: 1px solid var(--caos-color-border);
    border-radius: 999px;
    padding: 1px 6px;
  }
`);

function isBranch(n: CaosTreeNode): boolean {
  return Array.isArray(n.children);
}

/** Keep a node if its own label matches, or (for a branch) if any descendant survives. */
function filterNodes(nodes: readonly CaosTreeNode[], q: string): CaosTreeNode[] {
  if (!q) return [...nodes];
  const out: CaosTreeNode[] = [];
  for (const n of nodes) {
    if (isBranch(n)) {
      const kids = filterNodes(n.children!, q);
      if (kids.length) out.push({ ...n, children: kids });
    } else if (n.label.toLowerCase().includes(q)) {
      out.push(n);
    }
  }
  return out;
}

/** Every branch id in a tree, so `expandedIds` can be stated as what IS open rather than what is not. */
function branchIds(nodes: readonly CaosTreeNode[], into: Set<string> = new Set()): Set<string> {
  for (const n of nodes) {
    if (isBranch(n)) {
      into.add(n.id);
      branchIds(n.children!, into);
    }
  }
  return into;
}

/** How long a run of keystrokes counts as one type-ahead word. */
const TYPE_AHEAD_MS = 1000;

export class CaosTree extends CaosElement {
  static get observedAttributes(): string[] {
    return ['selected-id', 'filter', 'collapsible', 'filterable', 'find-label', 'find-placeholder'];
  }

  private headingHost: HTMLDivElement;
  private findHost: HTMLDivElement;
  private treeHost: HTMLUListElement;
  private message: HTMLParagraphElement;
  private _nodes: CaosTreeNode[] = [];
  private _heading?: CaosTreeHeading;
  /**
   * The find box, once built. Kept rather than rebuilt on every render: a list that redraws as you
   * type must not take the caret out of the box you are typing into.
   */
  private findInput: HTMLInputElement | null = null;
  /**
   * The branches the reader has CLOSED. Held as the closed set rather than the open one so that a
   * branch arriving in new data is open — the default the component promises — instead of hidden
   * because nobody had opened something that did not exist yet.
   */
  private _closed = new Set<string>();
  private typed = '';
  private typedAt = 0;

  /** How to word "nothing matches" for the live filter text. Host-owned wording; this component
   *  knows only that a filter came up empty, not what it is a filter over. */
  emptyFilterMessage: (term: string) => string = (term) => `No matches for “${term}”.`;

  constructor() {
    super(sheet);
    this.headingHost = document.createElement('div');
    this.headingHost.className = 'rail-heading';
    this.headingHost.hidden = true;
    this.findHost = document.createElement('div');
    this.findHost.className = 'find';
    this.findHost.hidden = true;
    this.treeHost = document.createElement('ul');
    this.treeHost.className = 'groups';
    this.treeHost.setAttribute('role', 'tree');
    this.message = document.createElement('p');
    this.message.className = 'none';
    this.message.hidden = true;
    this.root.append(this.headingHost, this.findHost, this.treeHost, this.message);
    this.addEventListener('keydown', (e) => this.onKeyDown(e));
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(): void {
    this.render();
  }

  /** The tree (data in). */
  get nodes(): CaosTreeNode[] {
    return this._nodes;
  }
  set nodes(list: CaosTreeNode[]) {
    this._nodes = Array.isArray(list) ? list : [];
    this.render();
  }

  /** The branches currently open, as ids. Setting it closes every branch not named. */
  get expandedIds(): string[] {
    return [...branchIds(this._nodes)].filter((id) => !this._closed.has(id));
  }
  set expandedIds(list: readonly string[]) {
    const open = new Set(Array.isArray(list) ? list : []);
    this._closed = new Set([...branchIds(this._nodes)].filter((id) => !open.has(id)));
    this.render();
  }

  /** CAOS-592 — the rail's heading (data in), or nothing for a bare list. */
  get heading(): CaosTreeHeading | undefined {
    return this._heading;
  }
  set heading(value: CaosTreeHeading | undefined) {
    this._heading = value && typeof value === 'object' ? value : undefined;
    this.render();
  }

  private get selectedId(): string | null {
    return this.getAttribute('selected-id');
  }

  /**
   * What the list is narrowed by: what somebody has typed into the find box this component draws,
   * else the `filter` attribute a host sets. A host that drives the filter itself keeps working
   * exactly as before; one that asks for the box hands the filtering over with it.
   */
  private get filterTerm(): string {
    if (this.findInput) return this.findInput.value.trim();
    return (this.getAttribute('filter') ?? '').trim();
  }

  private get collapsible(): boolean {
    return this.hasAttribute('collapsible');
  }

  /** A branch is open unless a collapsible tree has been told to close it; a filter opens every one. */
  private isOpen(id: string): boolean {
    if (!this.collapsible || this.filterTerm) return true;
    return !this._closed.has(id);
  }

  private emit(type: string, detail?: unknown): void {
    this.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true, detail }));
  }

  // ── The nodes a reader can currently reach, in the order they are read ──────

  /** Every `treeitem` on screen, top to bottom: a closed branch contributes itself and not its children. */
  private visibleItems(): HTMLLIElement[] {
    const out: HTMLLIElement[] = [];
    const walk = (list: Element): void => {
      for (const child of [...list.children]) {
        if (!(child instanceof HTMLLIElement) || child.getAttribute('role') !== 'treeitem') continue;
        out.push(child);
        if (child.getAttribute('aria-expanded') === 'false') continue;
        const sub = [...child.children].find((c) => c instanceof HTMLUListElement);
        if (sub) walk(sub);
      }
    };
    walk(this.treeHost);
    return out;
  }

  /** Move the single tab stop onto `li`, and take focus there when asked. */
  private setActive(li: HTMLLIElement | undefined, focus: boolean): void {
    if (!li) return;
    for (const other of this.treeHost.querySelectorAll<HTMLLIElement>('[role="treeitem"]')) {
      other.tabIndex = other === li ? 0 : -1;
    }
    if (focus) li.focus();
  }

  private toggle(li: HTMLLIElement, open: boolean): void {
    const id = li.dataset['nodeId'] ?? '';
    if (!this.collapsible || li.getAttribute('aria-expanded') === null) return;
    if (open) this._closed.delete(id);
    else this._closed.add(id);
    this.emit('tree-toggle', { id, expanded: open });
    this.render();
    this.setActive(
      this.visibleItems().find((x) => x.dataset['nodeId'] === id),
      true,
    );
  }

  private onKeyDown(ev: KeyboardEvent): void {
    const items = this.visibleItems();
    if (!items.length) return;
    const current = items.find((li) => li === (this.root.activeElement as HTMLElement | null));
    if (!current) return;
    const at = items.indexOf(current);
    const expanded = current.getAttribute('aria-expanded');

    switch (ev.key) {
      case 'ArrowDown':
        ev.preventDefault();
        this.setActive(items[Math.min(at + 1, items.length - 1)], true);
        return;
      case 'ArrowUp':
        ev.preventDefault();
        this.setActive(items[Math.max(at - 1, 0)], true);
        return;
      case 'Home':
        ev.preventDefault();
        this.setActive(items[0], true);
        return;
      case 'End':
        ev.preventDefault();
        this.setActive(items[items.length - 1], true);
        return;
      case 'ArrowRight':
        ev.preventDefault();
        // A closed branch opens; an open one hands focus to the first thing inside it.
        if (expanded === 'false') this.toggle(current, true);
        else if (expanded === 'true') this.setActive(items[at + 1], true);
        return;
      case 'ArrowLeft': {
        ev.preventDefault();
        // An open branch closes; anything else steps out to the branch holding it.
        if (expanded === 'true') {
          this.toggle(current, false);
          return;
        }
        const depth = Number(current.getAttribute('aria-level') ?? '1');
        for (let i = at - 1; i >= 0; i -= 1) {
          if (Number(items[i]!.getAttribute('aria-level') ?? '1') < depth) {
            this.setActive(items[i], true);
            return;
          }
        }
        return;
      }
      case 'Enter':
      case ' ': {
        // Space always opens or closes. Enter OPENS THE THING: a leaf, or a branch that is itself a
        // destination — otherwise a keyboard reader could expand a parent record but never reach it.
        const heading = [...current.children].find((c) => c.classList.contains('heading'));
        const link = heading ? [...heading.children].find((c) => c.classList.contains('heading__link')) : undefined;
        const row = link ?? [...current.children].find((c) => c.classList.contains('item'));
        if (expanded !== null && (ev.key === ' ' || !row)) {
          ev.preventDefault();
          this.toggle(current, expanded === 'false');
          return;
        }
        if (ev.key === 'Enter' && row) {
          ev.preventDefault();
          row.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
          return;
        }
        if (ev.key === ' ' && expanded !== null) {
          ev.preventDefault();
          this.toggle(current, expanded === 'false');
        }
        return;
      }
      default:
        break;
    }

    // Type-ahead: a run of printable keys jumps to the next label starting with them.
    if (ev.key.length !== 1 || ev.altKey || ev.ctrlKey || ev.metaKey) return;
    const now = Date.now();
    this.typed = now - this.typedAt > TYPE_AHEAD_MS ? ev.key : this.typed + ev.key;
    this.typedAt = now;
    const want = this.typed.toLowerCase();
    const order = [...items.slice(at + 1), ...items.slice(0, at + 1)];
    const hit = order.find((li) => (li.dataset['label'] ?? '').toLowerCase().startsWith(want));
    if (hit) {
      ev.preventDefault();
      this.setActive(hit, true);
    }
  }

  // ── Drawing ────────────────────────────────────────────────────────────────

  /**
   * CAOS-592 — the heading row. A heading carrying an href is a real link, for the same reason a row
   * is: middle-click and "open in new tab" are how people use a navigation. It reads as current when
   * the console's landing is what is on screen.
   */
  private renderHeading(): void {
    const heading = this._heading;
    this.headingHost.replaceChildren();
    this.headingHost.hidden = heading === undefined;
    if (!heading) return;
    const el = heading.href ? document.createElement('a') : document.createElement('div');
    el.className = 'rail-heading__link';
    if (el instanceof HTMLAnchorElement) el.href = heading.href!;
    // Current only once the console has SAID what is on screen. A row compares against the raw
    // attribute and so marks nothing while `selected-id` is absent; the heading answers the same
    // way, rather than reading an unset rail as though its landing were open.
    if (this.selectedId !== null && this.selectedId === (heading.id ?? '')) {
      el.setAttribute('aria-current', 'page');
    }
    if (heading.icon && isIconName(heading.icon)) {
      const glyph = document.createElement('span');
      glyph.className = 'rail-heading__glyph';
      glyph.setAttribute('aria-hidden', 'true');
      glyph.append(makeIcon(heading.icon));
      el.append(glyph);
    }
    const label = document.createElement('span');
    label.textContent = heading.label;
    el.append(label);
    if (el instanceof HTMLAnchorElement) {
      el.addEventListener('click', (ev) => {
        const e = ev as MouseEvent;
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        ev.preventDefault();
        this.emit('tree-choose', { id: heading.id ?? '', href: heading.href });
      });
    }
    this.headingHost.append(el);
  }

  /**
   * CAOS-592 — the find box, drawn only where it is asked for. It is built ONCE and kept: the list
   * redraws on every keystroke, and rebuilding the box would take the caret out of it.
   */
  private renderFind(): void {
    if (!this.hasAttribute('filterable')) {
      this.findHost.replaceChildren();
      this.findHost.hidden = true;
      this.findInput = null;
      return;
    }
    this.findHost.hidden = false;
    if (!this.findInput) {
      const glyph = document.createElement('span');
      glyph.className = 'find__glyph';
      glyph.setAttribute('aria-hidden', 'true');
      if (isIconName('search')) glyph.append(makeIcon('search'));
      const input = document.createElement('input');
      input.type = 'search';
      input.className = 'find__input';
      // A real named field rather than an anonymous input (CAOS-634). The id is scoped to this
      // shadow root, so every rail on a page can carry one without colliding.
      input.id = 'find';
      input.name = 'find';
      input.addEventListener('input', () => this.render());
      this.findInput = input;
      this.findHost.replaceChildren(glyph, input);
    }
    this.findInput.setAttribute('aria-label', this.getAttribute('find-label') ?? 'Quick Find');
    this.findInput.placeholder = this.getAttribute('find-placeholder') ?? 'Quick Find…';
  }

  private renderLeafRow(node: CaosTreeNode, depth: number): HTMLAnchorElement | HTMLButtonElement {
    const el = node.href ? document.createElement('a') : document.createElement('button');
    el.className = 'item';
    el.style.setProperty('--depth', String(depth));
    // The node holds the tab stop; the control inside it stays clickable but out of the tab order.
    el.tabIndex = -1;
    if (el instanceof HTMLAnchorElement) {
      el.href = node.href!;
    } else {
      el.type = 'button';
    }
    if (node.id === this.selectedId) {
      el.classList.add('is-active');
      el.setAttribute('aria-current', 'page');
    }
    if (node.icon && isIconName(node.icon)) {
      const glyph = document.createElement('span');
      glyph.className = 'item-glyph';
      glyph.setAttribute('aria-hidden', 'true');
      glyph.append(makeIcon(node.icon));
      el.append(glyph);
    }
    const label = document.createElement('span');
    label.className = 'item__label';
    label.textContent = node.label;
    el.append(label);
    if (node.badge) {
      const badge = document.createElement('span');
      badge.className = 'badge';
      badge.textContent = node.badge;
      el.append(badge);
    }
    el.addEventListener('click', (ev) => {
      if (el instanceof HTMLAnchorElement) {
        const e = ev as MouseEvent;
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        ev.preventDefault();
      }
      this.emit('tree-choose', { id: node.id, href: node.href });
    });
    return el;
  }

  private renderBranchRow(node: CaosTreeNode, depth: number): HTMLDivElement {
    const heading = document.createElement('div');
    heading.className = 'heading';
    heading.style.setProperty('--depth', String(depth));
    if (this.collapsible) {
      const twisty = document.createElement('span');
      twisty.className = 'twisty';
      twisty.setAttribute('aria-hidden', 'true');
      twisty.textContent = '▾';
      heading.append(twisty);
    }
    // A BRANCH CAN BE A DESTINATION (CAOS-591). A role that holds other roles is still a record
    // somebody opens. Before this, only a leaf could carry an href, so a hierarchy drawn with this
    // piece would have quietly lost the link on every parent — which is exactly the kind of thing
    // replacing hand-drawn markup with a component is supposed not to do.
    const label = document.createElement(node.href ? 'a' : 'span');
    label.className = node.href ? 'heading__link' : 'heading__label';
    label.textContent = node.label;
    if (label instanceof HTMLAnchorElement) {
      label.href = node.href!;
      // The node holds the tab stop; the link inside it stays clickable but out of the tab order.
      label.tabIndex = -1;
      label.addEventListener('click', (ev) => {
        const e = ev as MouseEvent;
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        ev.preventDefault();
        // Opening the record is not opening the branch: the heading's own toggle must not also fire.
        ev.stopPropagation();
        this.emit('tree-choose', { id: node.id, href: node.href });
      });
    }
    heading.append(label);
    if (node.badge) {
      const badge = document.createElement('span');
      badge.className = 'badge';
      badge.textContent = node.badge;
      heading.append(badge);
    }
    return heading;
  }

  /** One node as its `treeitem`, with its own `group` of children when it is a branch. */
  private renderNode(node: CaosTreeNode, depth: number, position: number, siblings: number): HTMLLIElement {
    const li = document.createElement('li');
    li.className = isBranch(node) ? 'node is-branch' : 'node';
    li.setAttribute('role', 'treeitem');
    li.setAttribute('aria-level', String(depth + 1));
    li.setAttribute('aria-posinset', String(position));
    li.setAttribute('aria-setsize', String(siblings));
    li.dataset['nodeId'] = node.id;
    li.dataset['label'] = node.label;
    li.tabIndex = -1;

    if (!isBranch(node)) {
      li.append(this.renderLeafRow(node, depth));
      if (node.id === this.selectedId) li.setAttribute('aria-selected', 'true');
      return li;
    }

    const open = this.isOpen(node.id);
    // A tree that cannot be collapsed says nothing about expansion rather than claiming to open.
    if (this.collapsible) li.setAttribute('aria-expanded', String(open));
    const heading = this.renderBranchRow(node, depth);
    if (this.collapsible) {
      heading.addEventListener('click', () => this.toggle(li, !this.isOpen(node.id)));
    }
    li.append(heading);
    if (open) li.append(this.renderList(node.children ?? [], depth + 1));
    return li;
  }

  private renderList(nodes: readonly CaosTreeNode[], depth: number): HTMLUListElement {
    const list = document.createElement('ul');
    list.className = 'list';
    list.setAttribute('role', 'group');
    nodes.forEach((n, i) => list.append(this.renderNode(n, depth, i + 1, nodes.length)));
    return list;
  }

  private render(): void {
    this.renderHeading();
    this.renderFind();
    this.treeHost.replaceChildren();
    const term = this.filterTerm;
    const visible = filterNodes(this._nodes, term.toLowerCase());

    // The empty-filter message REPLACES the tree rather than sitting under an empty one, so a reader
    // is never offered a `tree` role with nothing in it to move through.
    const nothing = term.length > 0 && visible.length === 0;
    this.message.hidden = !nothing;
    this.treeHost.hidden = nothing;
    if (nothing) {
      this.message.textContent = this.emptyFilterMessage(term);
      return;
    }

    visible.forEach((n, i) => this.treeHost.append(this.renderNode(n, 0, i + 1, visible.length)));

    // Exactly one tab stop: the selected node when it is on screen, otherwise the first one.
    const items = this.visibleItems();
    const selected = items.find((li) => li.dataset['nodeId'] === this.selectedId);
    this.setActive(selected ?? items[0], false);
  }
}

define('caos-tree', CaosTree);

declare global {
  interface HTMLElementTagNameMap {
    'caos-tree': CaosTree;
  }
}
